import { selectFinalGuidanceReport } from '@/features/report/selectFinalGuidanceReport'
import type { ConsentFlow } from '@/ordering/consentFlow'
import { OrderStatusDisplay } from '@/types/NavigationTypes'
import type { OrderReportResponse, ReanalysisCountResponse } from '@/types/OrderReportTypes'
import { CollectionType, TestType } from '@/types/RequisitionFormTypes'

/** Whether turnaround times in test options are expressed as calendar or business days. */
export type TurnaroundTimeDayBasis = 'calendar' | 'business'

/**
 * Matches CareBus / provider-api ``OrderingDomainConfig.consentFlow`` (UPPER_CASE).
 * Derived from the {@link ConsentFlow} enum so the wire values live in one place; config
 * JSON still supplies plain strings, which this template-literal type accepts.
 */
export type OrderingConsentFlow = `${ConsentFlow}`

export const DEFAULT_TURNAROUND_TIME_DAY_BASIS: TurnaroundTimeDayBasis = 'calendar'

const normalizeTurnaroundTimeDayBasis = (value: unknown): TurnaroundTimeDayBasis => {
  if (value === 'calendar' || value === 'business') {
    return value
  }
  return DEFAULT_TURNAROUND_TIME_DAY_BASIS
}

export interface FeatureFlags {
  delegation: boolean
  aiPhenotypes: boolean
  help: boolean
  allowFamilyHistoryToggle: boolean
  guidance: boolean
  /**
   * Controls whether providers can edit sequencing options (e.g. add a
   * comparator) on a submitted, pre-accession order. When false, the
   * "Add Comparator" item in the Edit Order menu and the Sequencing Options
   * card's Edit button are hidden on the post-submit Order page. Defaults to
   * false so organizations must explicitly opt-in.
   */
  allowPostSubmitComparatorEdits: boolean
  /**
   * When true, CareBus ``consenter_created`` runs after submit so family-portal can
   * provision Auth0 users and send welcome flows. Must match microapp
   * ``INFLECTION_WEB_ORDERING.featureFlags.sendFamilyWelcomeEmail``.
   */
  sendFamilyWelcomeEmail?: boolean
  /**
   * R5 "Order Start (side nav)" layout. When true, OrderStart renders a left
   * section-navigation panel beside the form cards. When false/missing, the
   * default single-column layout is used. Opt-in so the new layout can roll out
   * per organization.
   */
  orderSideNav?: boolean
  /**
   * Collection-kit ordering. When true, the comparator add/section-editor
   * modals expose the "Request collection kit" toggle + shipment address
   * sub-form, whose presence makes provider-api emit a ``kit_shipment.requested``
   * event. When false/missing, the kit-request UI is hidden and no kit shipment
   * is requested. Opt-in so kit ordering can roll out per organization. Must
   * match microapp ``INFLECTION_WEB_ORDERING.featureFlags.kitOrdering``.
   */
  kitOrdering?: boolean
  /**
   * Case Connect (Clinician Connect) collaboration. When true, an org's members
   * see the Case Connect entry points — the "Case Connect" / "New Case Connect"
   * affordances on the guidance page and the "Case Connect" nav link. When
   * false/missing, those org-member entry points are hidden (opt-in per org).
   * NOTE: this per-org flag does NOT gate org-LESS external case participants:
   * they have no client config, so their case list (`/cases`) and a specific
   * case (`/cases/:caseId`) remain permission-gated only (an invited external
   * clinician must always reach their case). Must match microapp
   * ``INFLECTION_WEB_ORDERING.featureFlags.caseConnect``.
   */
  caseConnect?: boolean
  /** Disable automatic extraction while retaining the explicit Generate action. */
  buttonOnlyHpoExtraction?: boolean
  /**
   * Gates the clinical-note document uploader (``FileUploadPanel`` via
   * ``ClinicalNoteAttachments``) in the clinical summary card. Ships OFF until
   * the docMgmt ``/documents`` proxy + CORS are live in every environment.
   */
  clinicalNoteAttachments?: boolean
}

export interface ValidationRuleSet {
  requireClinicalSummary?: boolean
  requirePhenotypes?: boolean
  requireFamilyHistory?: boolean
  requireParentsWhenRequested?: boolean
  customEmailRegex?: string
  customPhoneRegex?: string
}

export interface UiTestTypeOption {
  value: TestType
  label: string
  code: string
  category: string
  turnaroundTimeInDays: number
  /**
   * Whether `turnaroundTimeInDays` for this test is calendar or business days.
   * Drives Test Options copy and turnaround cards for the selected test.
   */
  turnaroundTimeDayBasis?: TurnaroundTimeDayBasis
  requiresParentSamples: boolean
  parentSamplesRequired: 0 | 1 | 2 | 3
  probandSampleTypes?: CollectionType[]
  /** Collection types allowed for parent samples; used for parent collection type options in form and modals. */
  parentSampleTypes?: CollectionType[]
  /** When true, this test type is selected by default when creating a new order. Exactly one should be true. */
  isDefault?: boolean
  /**
   * Per–test-line consent orchestration; maps to ordering-domain ``tests[].consentFlowId`` when
   * building submit catalog from UI config. Omit or use org ``UiConfig.consentFlow`` default downstream.
   */
  consentFlow?: OrderingConsentFlow
}

export interface ThemeTokens {
  brandPrimary?: string
  brandStrong?: string
  textBrand?: string
  accent?: string
}

/**
 * Consent config types are discriminated on `isApplicable`. When `false`, only
 * the discriminant is required — the config-driven copy is meaningless for a
 * hidden section (the copy fields remain optionally present so legacy
 * full-shape config rows stay valid). When `true`, the copy fields are
 * required. Consumers MUST narrow on `isApplicable` before reading copy;
 * `ConsentAccordionGroup` and `ComparatorParentConsentOverviewModal` already do.
 */

/** Requisition incidental findings Opt-In / Opt-Out copy (config-driven). */
interface IncidentalFindingsConsentCopy {
  sectionTitle: string
  optInLabel: string
  optOutLabel: string
  /** IMDB secondary_consent key when persisted server-side. */
  secondaryConsentKey?: string
  /**
   * Inline HTML for disclosure below the checkboxes (e.g. `<strong>` for emphasis). Sanitized at render.
   */
  disclosureHtml: string
  /**
   * UI gating hint: when ``true``, the provider must actively pick Opt-In or
   * Opt-Out for this section before the order can be submitted — a defaulted /
   * untouched control does not count as answered. Absent/``null`` → ``false``
   * (today's behavior: no forced interaction). Not echoed back to the API.
   */
  requireInteraction?: boolean
}
export type IncidentalFindingsConsentConfig =
  | ({ isApplicable: true } & IncidentalFindingsConsentCopy)
  | ({ isApplicable: false } & Partial<IncidentalFindingsConsentCopy>)

/** New York sample retention & de-identified use Opt-In copy (config-driven). */
interface NewYorkSampleRetentionConsentCopy {
  sectionTitle: string
  optInLabel: string
  /** IMDB secondary_consent key when persisted server-side. */
  secondaryConsentKey?: string
  /**
   * Inline HTML for disclosure body (e.g. `<strong>` for emphasis). Sanitized at render.
   */
  disclosureHtml: string
  /**
   * UI gating hint: when ``true``, the provider must actively pick a selection
   * for this section before submit. Absent/``null`` → ``false`` (today's
   * behavior). Not echoed back to the API.
   */
  requireInteraction?: boolean
}
export type NewYorkSampleRetentionConsentConfig =
  | ({ isApplicable: true } & NewYorkSampleRetentionConsentCopy)
  | ({ isApplicable: false } & Partial<NewYorkSampleRetentionConsentCopy>)

/** Comparator biological parent: research contact opt-in copy (config-driven). */
interface ResearchParticipationContactConsentCopy {
  sectionTitle: string
  optInLabel: string
  /** IMDB secondary_consent key when persisted server-side. */
  secondaryConsentKey?: string
  disclosureHtml: string
  /**
   * UI gating hint: when ``true``, the provider must actively pick a selection
   * for this section before submit. Absent/``null`` → ``false`` (today's
   * behavior). Not echoed back to the API.
   */
  requireInteraction?: boolean
}
export type ResearchParticipationContactConsentConfig =
  | ({ isApplicable: true } & ResearchParticipationContactConsentCopy)
  | ({ isApplicable: false } & Partial<ResearchParticipationContactConsentCopy>)

/**
 * Single entry in the microapp ``INFLECTION_WEB_ORDERING.patientConsents`` array.
 *
 * The microapp emits ordered consents by array index (Incidental Findings, New
 * York Sample Retention, Research Participation Contact). Consumers should
 * derive the legacy semantic triple via :func:`getPatientConsents` rather than
 * indexing the array directly so the lookup remains stable as the array grows.
 */
export interface PatientConsentItem {
  isApplicable: boolean
  sectionTitle: string
  optInLabel: string
  /** Optional: NY/Research consents historically had no opt-out toggle. */
  optOutLabel?: string
  /**
   * IMDB `secondary_consent.secondary_consent_key` (e.g. INCIDENTAL_FINDINGS).
   * From microapp config; server falls back to positional defaults if omitted.
   */
  secondaryConsentKey?: string
  disclosureHtml: string
  /**
   * UI gating hint (client-side only): when ``true``, the provider must
   * actively pick a selection for this section before the order can be
   * submitted. A defaulted/untouched control does not count as answered.
   * Absent/``null`` → ``false`` (today's behavior: no forced interaction).
   * Passed through the ``/config/ui`` payload untouched by provider-api and is
   * NOT echoed back to the API on submit.
   */
  requireInteraction?: boolean
}

/**
 * Configurable behavior for buttons on the order success modal.
 *
 * ``action`` is a typed enum so the SPA decides what to do when the button is
 * clicked — config never carries arbitrary URLs or JavaScript.
 *
 * - ``printOrderForm`` — open the requisition form for printing.
 * - ``returnToDashboard`` — navigate to the orders list.
 * - ``closeModal`` — dismiss the modal only.
 * - ``navigateInProgress`` — legacy default; navigates to the in-progress
 *   order workspace.
 * - ``generateShippingLabel`` — request a FedEx kit shipping label via
 *   ``POST /kits/shipping-label`` (addressFrom = ``orgAddress``, addressTo =
 *   ``wetLab.shipToAddress``) and open the ShippingLabelModal to preview /
 *   print / download the returned label.
 */
export type OrderSuccessButtonAction =
  'printOrderForm' | 'returnToDashboard' | 'closeModal' | 'navigateInProgress' | 'generateShippingLabel'

/**
 * Visual style for an order-success button. ``primary`` = filled brand,
 * ``secondary`` = outlined. Defaults to ``secondary`` when omitted in the
 * structured ``buttons`` list. Multiple ``primary`` buttons are allowed.
 */
export type OrderSuccessButtonVariant = 'primary' | 'secondary'

export interface OrderSuccessButtonConfig {
  enabled?: boolean
  label?: string
  action?: OrderSuccessButtonAction
  /**
   * Visual style: ``primary`` (filled brand) or ``secondary`` (outlined).
   * Only meaningful inside the ``buttons`` array; defaults to ``secondary``
   * when omitted. The legacy ``primaryButton``/``secondaryButton`` fields
   * derive their style from their slot, not this field.
   */
  variant?: OrderSuccessButtonVariant
}

/**
 * Structured order-success modal content. Optional in the config; when absent
 * the modal falls back to the legacy ``orderSuccessTitle`` / ``orderSuccessMessage``
 * / ``orderSuccessButtonLabel`` flat fields below.
 */
export interface OrderSuccessConfig {
  /** Modal heading. */
  title?: string
  /** Sub-heading shown below the title. */
  subtitle?: string
  /**
   * Rich-text body. Allowed tags: ``<p>``, ``<strong>``, ``<em>``, ``<ul>``,
   * ``<ol>``, ``<li>``, ``<h1>``-``<h6>``, ``<a href>``. Sanitized at render
   * via :func:`sanitizeRichHtml`.
   */
  bodyHtml?: string
  /** Primary (filled, brand-colored) button. */
  primaryButton?: OrderSuccessButtonConfig
  /** Secondary (outlined) button shown next to the primary one. */
  secondaryButton?: OrderSuccessButtonConfig
  /**
   * Ordered, flexible button list (preferred over the legacy
   * ``primaryButton``/``secondaryButton`` pair). When present and non-empty
   * the modal renders exactly these buttons, in array order, per
   * CONTRACT_MULTIBUTTON §2:
   *
   * - each entry's ``enabled`` defaults to ``true``; ``enabled: false`` is not
   *   rendered;
   * - ``label`` falls back to a per-action default when blank;
   * - an entry with no resolvable ``action`` is skipped (never a dead button);
   * - ``variant`` styles the button (``primary`` filled / ``secondary``
   *   outlined), defaulting to ``secondary``; multiple ``primary`` allowed.
   *
   * Degenerate cases: ``buttons: []`` (present but empty) renders NO action
   * buttons — an explicit "none", NOT a legacy fallback. When ``buttons`` is
   * absent/undefined the modal resolves the legacy
   * ``primaryButton``/``secondaryButton`` pair exactly as before.
   */
  buttons?: OrderSuccessButtonConfig[]
}

/**
 * A fully-resolved order-success button ready to render: it always has a
 * concrete, dispatchable ``action``, a non-blank ``label``, and a ``variant``.
 * Produced by :func:`resolveOrderSuccessButtons`.
 */
export interface ResolvedOrderSuccessButton {
  label: string
  action: OrderSuccessButtonAction
  variant: OrderSuccessButtonVariant
}

/** Per-action default labels (CONTRACT_MULTIBUTTON §2). */
const ORDER_SUCCESS_DEFAULT_LABELS: Record<OrderSuccessButtonAction, string> = {
  printOrderForm: 'Print Order Form',
  returnToDashboard: 'Return to Dashboard',
  generateShippingLabel: 'Generate Shipping Label',
  closeModal: 'Close',
  navigateInProgress: 'Continue',
}

const ORDER_SUCCESS_ACTIONS = Object.keys(ORDER_SUCCESS_DEFAULT_LABELS) as OrderSuccessButtonAction[]

const isOrderSuccessButtonAction = (value: unknown): value is OrderSuccessButtonAction =>
  typeof value === 'string' && (ORDER_SUCCESS_ACTIONS as string[]).includes(value)

/** Legacy modal defaults, kept in sync with :func:`resolveOrderSuccessButtons`. */
const DEFAULT_LEGACY_PRIMARY_LABEL = 'Print Order Form'
const DEFAULT_LEGACY_SECONDARY_LABEL = 'Return to Dashboard'

/**
 * Resolve the ordered list of order-success buttons to render, implementing
 * CONTRACT_MULTIBUTTON §2 precedence and the §Degenerate contracts.
 *
 * 1. When ``orderSuccess.buttons`` is present AND non-empty → render exactly
 *    those, in array order. Each entry: ``enabled`` defaults true (``false`` is
 *    skipped); a blank ``label`` falls back to the per-action default; an entry
 *    with no resolvable ``action`` (missing or unknown value) is skipped — never
 *    a dead button; ``variant`` defaults to ``secondary``. An all-disabled /
 *    all-invalid list resolves to ``[]`` (explicit none, NOT legacy fallback).
 * 2. When ``orderSuccess.buttons`` is present but empty (``[]``) → ``[]``
 *    (explicit none, NOT legacy fallback).
 * 3. When ``orderSuccess.buttons`` is absent/undefined → the legacy
 *    ``secondaryButton`` (rendered first) + ``primaryButton`` pair, resolved
 *    with the same defaults the modal used before this change (so existing
 *    tenants render identically). ``hasStructuredOrderSuccess`` mirrors the
 *    modal's "an ``orderSuccess`` block exists" flag: when false, only a single
 *    ``navigateInProgress`` primary (labelled ``legacyButtonLabel``) renders,
 *    matching the un-migrated single-button shape.
 *
 * The returned buttons are in render order (left→right): for the legacy pair
 * that is secondary then primary, matching today's modal layout.
 */
export const resolveOrderSuccessButtons = (
  orderSuccess: OrderSuccessConfig | undefined,
  opts: { hasStructuredOrderSuccess: boolean; legacyButtonLabel: string }
): ResolvedOrderSuccessButton[] => {
  const { hasStructuredOrderSuccess, legacyButtonLabel } = opts
  const buttons = orderSuccess?.buttons

  // Cases 1 & 2: an explicit ``buttons`` array is authoritative (never legacy).
  if (Array.isArray(buttons)) {
    const resolved: ResolvedOrderSuccessButton[] = []
    for (const entry of buttons) {
      if (entry?.enabled === false) continue
      const action = entry?.action
      if (!isOrderSuccessButtonAction(action)) continue
      const label = entry?.label?.trim() || ORDER_SUCCESS_DEFAULT_LABELS[action]
      const variant: OrderSuccessButtonVariant = entry?.variant === 'primary' ? 'primary' : 'secondary'
      resolved.push({ label, action, variant })
    }
    return resolved
  }

  // Case 3: legacy primaryButton/secondaryButton pair (back-compat, unchanged).
  const primary = resolveLegacyButton(orderSuccess?.primaryButton, {
    enabled: true,
    label: hasStructuredOrderSuccess ? DEFAULT_LEGACY_PRIMARY_LABEL : legacyButtonLabel,
    action: hasStructuredOrderSuccess ? 'printOrderForm' : 'navigateInProgress',
  })
  const secondary = resolveLegacyButton(orderSuccess?.secondaryButton, {
    enabled: hasStructuredOrderSuccess,
    label: DEFAULT_LEGACY_SECONDARY_LABEL,
    action: 'returnToDashboard',
  })

  const legacy: ResolvedOrderSuccessButton[] = []
  // Render order matches today's modal: secondary first, then primary.
  if (secondary.enabled) {
    legacy.push({ label: secondary.label, action: secondary.action, variant: 'secondary' })
  }
  if (primary.enabled) {
    legacy.push({ label: primary.label, action: primary.action, variant: 'primary' })
  }
  return legacy
}

const resolveLegacyButton = (
  cfg: OrderSuccessButtonConfig | undefined,
  fallback: { label: string; action: OrderSuccessButtonAction; enabled: boolean }
): { enabled: boolean; label: string; action: OrderSuccessButtonAction } => ({
  enabled: cfg?.enabled ?? fallback.enabled,
  label: cfg?.label?.trim() || fallback.label,
  action: cfg?.action ?? fallback.action,
})

/** Provider consent and EHR signing reminder copy for the Order Ready to Send page. */
export interface ProviderConsent {
  /** Provider Consent text shown above provider name and NPI. */
  providerConsentText?: string
  /** Title for the EHR sign reminder banner. */
  ehrSignReminderTitle?: string
  /** Body text for the EHR sign reminder banner. */
  ehrSignReminderBody?: string
  /** Title for the order success modal (shown after submit). Legacy; prefer ``orderSuccess.title``. */
  orderSuccessTitle?: string
  /** Message/body for the order success modal. Legacy; prefer ``orderSuccess.bodyHtml``. */
  orderSuccessMessage?: string
  /** Label for the order success modal primary button. Legacy; prefer ``orderSuccess.primaryButton.label``. */
  orderSuccessButtonLabel?: string
  /** Structured order-success modal content (preferred). */
  orderSuccess?: OrderSuccessConfig
  /** R5 submit modal: line above expandable attestations. */
  submitOrderAttestationIntro?: string
  /** R5: collapsed summary title + body for clinical attestation accordion. */
  clinicalAttestationTitle?: string
  clinicalAttestationSummary?: string
  /** R5: Inflection / platform authorization accordion. */
  inflectionAuthorizationTitle?: string
  inflectionAuthorizationSummary?: string
  /** Full text shown when expanding Inflection authorization. */
  inflectionAuthorizationTerms?: string
}

export interface AssetConfig {
  logo?: string
}

export interface UiOrderStatusStepConfig {
  id: string
  label: string
}

/**
 * Per-order reanalysis cap from microapp ``WET_LAB.reanalysisLimit`` (projected
 * by provider-api). Always present whenever :interface:`UiWetLabConfig` is
 * emitted, with both sub-fields carrying fail-safe defaults — the SPA never has
 * to null-check them.
 *
 * NOTE: Informational only today. Enforcement is not yet wired server-side, so
 * the API still allows reanalysis regardless of ``maximumReanalysisCount``. Read
 * via :func:`getReanalysisLimit` / :func:`isReanalysisMaximumEnforced` /
 * :func:`getMaximumReanalysisCount`, which re-apply the fail-safe coercions so
 * consumers behave identically on legacy payloads that predate this block.
 */
export interface UiReanalysisLimitConfig {
  /**
   * Whether the per-order reanalysis cap is enforced. Only ``true`` when the
   * source is a real boolean ``true``; anything else (missing, ``null``, the
   * string ``"true"``, etc.) resolves to ``false``.
   */
  enforceReanalysisMaximumCount?: boolean
  /**
   * Max reanalyses permitted per order. Defaults to ``0`` when missing,
   * ``null``, a boolean, or a non-integer. A missing cap is a hard ``0``, not
   * "unlimited".
   */
  maximumReanalysisCount?: number
}

/** ``WET_LAB`` area projected by provider-api ``microapp_values_to_ui_config`` (sole source for TRF / lab display). */
export interface UiWetLabConfig {
  labName?: string
  cliaLabId?: string
  /**
   * Order fulfillment strategy (lowercased by the server). Drives whether the
   * SPA exposes the Cancel Order flow: only ``'inflection_test'`` supports
   * cancellation today. Absent on older configs — the SPA then infers from
   * ``labName`` using the same rules as the server resolver.
   */
  fulfillmentStrategy?: 'rcigm' | 'inflection_test'
  /**
   * Wet-lab ship-to (destination) address for kit shipping labels. Mapped to
   * the ``addressTo`` field of ``POST /kits/shipping-label`` (see
   * :func:`getShipToAddress`). Optional — the ``generateShippingLabel``
   * order-success action shows a config error when this is missing/incomplete
   * rather than calling the label endpoint. Set by client-config
   * ``WET_LAB.shipToAddress``.
   */
  shipToAddress?: UiAddressConfig
  /**
   * Per-order reanalysis cap (``WET_LAB.reanalysisLimit``). Always present
   * whenever ``wetLab`` is emitted; both sub-fields carry fail-safe defaults.
   * Read via :func:`getReanalysisLimit` rather than indexing directly so the
   * fail-safe coercions also apply to legacy payloads that omit the block.
   */
  reanalysisLimit?: UiReanalysisLimitConfig
}

/**
 * Organization mailing address from microapp ``CONTACTS`` (projected by
 * provider-api as ``orgAddress``). Used for TRF Section B; keys match
 * ``OrderFormInfoData.institution.address`` / PDF view-model.
 */
export interface UiOrgAddressConfig {
  street?: string
  /** Suite / unit / floor from microapp ``CONTACTS.addressLine2`` (merged into TRF ``STREET`` when set). */
  street2?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

/**
 * Postal address for kit shipping labels. Mirrors :interface:`UiOrgAddressConfig`
 * (street, street2, city, state, zip, country) and adds the optional recipient
 * fields Shippo accepts (name, phone, email). Used for both endpoints of
 * ``POST /kits/shipping-label``: ``addressFrom`` (from ``orgAddress``) and
 * ``addressTo`` (from ``wetLab.shipToAddress``). All fields optional at the
 * type level; :func:`isCompleteShippingAddress` enforces the required subset
 * before the client calls the label endpoint.
 */
export interface UiAddressConfig {
  name?: string
  street?: string
  street2?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  phone?: string
  email?: string
}

export interface UiConfig {
  id: string
  version: string
  /** Optional top-level schema version from client-config API payload. */
  schemaVersion?: number
  /**
   * Microapp-managed display name for the active client/organization
   * (lifted from ``GENERAL.clientName`` by the provider-api translator).
   * Primary source for navbar; top-level API ``client_name`` is merged in when
   * missing, and the navbar can fall back to Auth0 ``org_name``.
   */
  clientName?: string
  featureFlags: FeatureFlags
  testTypes: UiTestTypeOption[]
  validationRules: ValidationRuleSet
  themeTokens?: ThemeTokens
  /** Provider consent and EHR reminder copy for the Order Ready to Send page. */
  providerConsent?: ProviderConsent
  /**
   * Canonical microapp shape for the three requisition consents (array index
   * order: Incidental Findings, NY Sample Retention, Research Participation
   * Contact). Consumers should read via :func:`getPatientConsents`. The SPA
   * renders Incidental, then Research, then NY.
   */
  patientConsents?: PatientConsentItem[]
  /**
   * Legacy flat consent blocks from older ui_config payloads. Prefer
   * ``patientConsents`` / ``getPatientConsents`` for new data.
   */
  incidentalFindingsConsent?: IncidentalFindingsConsentConfig
  newYorkSampleRetentionConsent?: NewYorkSampleRetentionConsentConfig
  researchParticipationContactConsent?: ResearchParticipationContactConsentConfig
  assets?: AssetConfig
  orderStatusSteps?: UiOrderStatusStepConfig[]
  /** Lab display name and CMS CLIA id from microapp ``WET_LAB`` (set by provider-api). */
  wetLab?: UiWetLabConfig
  /** Organization address from microapp ``CONTACTS`` (set by provider-api). */
  orgAddress?: UiOrgAddressConfig
  /** Org default consent flow; aligns with GET /config/ordering-domain ``consentFlow``. */
  consentFlow?: OrderingConsentFlow
  /**
   * Config Console consent to test mode (``CONSENT_METHOD_A`` / ``CONSENT_METHOD_B`` /
   * ``CONSENT_METHOD_AB`` / ``CONSENT_METHOD_C``). Only ``CONSENT_METHOD_AB`` changes the
   * resolved consent flow; see ``resolveEffectiveConsentFlowForSubmit``.
   */
  consentMethod?: string
  /**
   * Coverage-program configuration for the org (advisory checks like CCS). Always
   * present on ``GET /config/ui/{org}`` responses per the contract, but modeled as
   * optional here so the fallback config and older payloads stay valid. Consumers
   * should read via :func:`isCcsCheckEnabled` rather than indexing this directly.
   */
  coverage?: CoverageConfig
  /**
   * Post-submit comparator-addition window (``ui_config.editabilityWindow``).
   * Optional: absent on unmigrated tenant configs and on deployed
   * dev/staging/prod microapp configs until they add the block. Read via
   * :func:`getEditabilityWindow` / :func:`isComparatorAdditionWindowEnabled`,
   * which default to disabled when the object is missing.
   */
  editabilityWindow?: EditabilityWindowConfig
  /**
   * Per-organization ordering-site tracking (GEN-5738), passed straight through
   * from the microapp ``INFLECTION_WEB_ORDERING.organizationSiteInformation``
   * block. When ``trackOrderSite`` is true the SPA renders a REQUIRED ordering-
   * site dropdown (options from ``organizationSites[].siteName``) and writes the
   * chosen name into ``canonicalOrder.orderingSite``; the provider-api enforces
   * the site at draft-create and allowlist-validates it on create/patch.
   *
   * Optional: absent on non-CHCM tenants (and unmigrated configs), so consumers
   * MUST treat a missing block as "tracking disabled" (read via
   * :func:`isOrderSiteTrackingEnabled` / :func:`getOrganizationSiteNames`).
   */
  organizationSiteInformation?: OrganizationSiteInformationConfig
  /**
   * DocuSign TRF e-signature configuration, passed straight through from the
   * microapp ``INFLECTION_WEB_ORDERING.docusign`` block. ``enabled`` turns TRF
   * e-signature on — the switch provider-api's ``docusign_enabled`` gates on —
   * and is what tells the SPA an order will receive a signature envelope
   * (so the TRF signature poll should run). Absent on orgs not
   * migrated to the DocuSign schema; read via :func:`isTrfSignatureDocusignEnabled`,
   * which fails closed (disabled) when the block is missing.
   */
  docusign?: DocusignConfig
  /**
   * FHIR identifier system OID (e.g. ``urn:oid:1.2.840.114350.1.13.0.1.7.4.836982.6000``)
   * from microapp ``HEALTH_SYSTEM_TECH_INTEGRATION.providerCredentialSystemId``, surfaced
   * top-level on ``ui_config`` by provider-api. Identifies which
   * ``Practitioner.qualification`` coding holds the provider's credential/title
   * (e.g. "MD"/"DO") for EHR practitioner reads.
   *
   * Optional: absent when the org hasn't configured it (or it is blank), so consumers MUST
   * treat it as optional and fall back to the first qualification's ``code.text``. Passed to
   * ``readPractitionerById`` / ``searchPractitioners`` in `practitionerSearchApi`.
   */
  providerCredentialSystemId?: string
}

/**
 * Microapp ``INFLECTION_WEB_ORDERING.organizationSiteInformation`` block
 * (GEN-5738), surfaced to the SPA so it can render the required ordering-site
 * dropdown for organizations that track ordering sites (e.g. CHCM).
 *
 * Phase 1 identifies a site by NAME only — there is no stable site code yet
 * (that is Phase 2 / GEN-5739). ``organizationSites[].siteName`` is both the
 * option label and the value written into ``canonicalOrder.orderingSite``.
 */
export interface OrganizationSite {
  /** Display label AND stored value for the site (Phase 1: name is the identifier). */
  siteName: string
}

export interface OrganizationSiteInformationConfig {
  /** Master switch: when true, the SPA renders + requires the ordering-site dropdown. */
  trackOrderSite?: boolean
  /** Allowlist of selectable sites; the dropdown options are sourced from here. */
  organizationSites?: OrganizationSite[]
}

/**
 * Microapp ``INFLECTION_WEB_ORDERING.docusign`` block, surfaced to the SPA
 * so it can tell whether an order participates in the TRF e-signature flow.
 * Only the fields the SPA reads are typed; the microapp block also carries
 * hospital-notification email lists the SPA does not use.
 */
export interface DocusignConfig {
  /** True when DocuSign TRF signing is on for the org. */
  enabled?: boolean
}

/**
 * Coverage-program configuration block from ``ui_config.coverage`` (sourced from
 * microapp ``HEALTH_SYSTEM_TECH_INTEGRATION.coverageInformation``). Display-only:
 * the actual eligibility gate is enforced server-side. New coverage flags will be
 * added here over time.
 */
export interface CoverageConfig {
  /**
   * Master switch for the CCS (California Children's Services) advisory coverage
   * check, gated per-tenant. When false/absent, CCS is effectively off for the
   * org: the admin CCS eligibility toggle and clinician-list CCS column are
   * hidden, ``GET /auth/users/clinicians`` returns ``ccsEligible: null``, and
   * ``POST /ordering/sessions/{id}/coverage-check`` returns no checks.
   */
  ccsCheckEnabled?: boolean
}

/**
 * Post-submit comparator-addition window from ``ui_config.editabilityWindow``
 * (sourced from microapp ``INFLECTION_WEB_ORDERING.editabilityWindow``).
 *
 * May be absent: older/unmigrated tenant configs — and deployed
 * dev/staging/prod microapp configs until they add the block — won't include
 * it. Consumers MUST treat a missing object as "window disabled" (read via
 * :func:`getEditabilityWindow` / :func:`isComparatorAdditionWindowEnabled`).
 *
 * Note: the pre-existing ``featureFlags.allowPostSubmitComparatorEdits`` flag
 * is separate and NOT driven by this object. The two overlap conceptually;
 * which one gates the comparator-add UI going forward is still to be confirmed
 * with the team. For now this is plumbing only.
 */
export interface EditabilityWindowConfig {
  /** Whether the post-submit comparator-addition window is active for the tenant. */
  editabilityWindowEnabled?: boolean
  /** Number of hours after order submit during which comparators may still be added. */
  comparatorAdditionWindowHours?: number
}

/**
 * Sentinel ``id`` used by :data:`LOGIN_FALLBACK_UI_CONFIG` so callers (e.g. Epic
 * callback) can distinguish "no config loaded yet" from a real microapp payload.
 */
export const FALLBACK_UI_CONFIG_ID = '__fallback_ui_config__'

/**
 * Minimal, explicitly-named UI config for **public** routes (login, callback,
 * verify-email) and as the loading-state placeholder. NOT a merge target —
 * authenticated routes must receive a complete payload from provider-api and
 * fail loud (``status: 'error'``) when validation fails.
 */
export const LOGIN_FALLBACK_UI_CONFIG: UiConfig = {
  id: FALLBACK_UI_CONFIG_ID,
  version: '0.0.0',
  featureFlags: {
    delegation: false,
    aiPhenotypes: false,
    help: false,
    allowFamilyHistoryToggle: false,
    guidance: false,
    allowPostSubmitComparatorEdits: false,
    sendFamilyWelcomeEmail: false,
    orderSideNav: false,
    kitOrdering: false,
    caseConnect: false,
    buttonOnlyHpoExtraction: false,
    clinicalNoteAttachments: false,
  },
  testTypes: [],
  validationRules: {},
}

/**
 * Validating parser for the ``ui_config`` blob returned by
 * ``GET /config/ui/{org_id}``. Throws :class:`InvalidUiConfigError` when
 * required fields are missing so :class:`UiConfigProvider` can transition to
 * ``status: 'error'`` instead of silently merging defaults.
 */
export class InvalidUiConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidUiConfigError'
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const requireString = (raw: Record<string, unknown>, key: string): string => {
  const value = raw[key]
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new InvalidUiConfigError(`ui_config.${key} is required (string)`)
  }
  return value
}

export const assertUiConfig = (raw: unknown): UiConfig => {
  if (!isPlainObject(raw)) {
    throw new InvalidUiConfigError('ui_config must be a JSON object')
  }
  const id = requireString(raw, 'id')
  const version = requireString(raw, 'version')
  if (!isPlainObject(raw.featureFlags)) {
    throw new InvalidUiConfigError('ui_config.featureFlags is required (object)')
  }
  if (!Array.isArray(raw.testTypes) || raw.testTypes.length === 0) {
    throw new InvalidUiConfigError('ui_config.testTypes is required (non-empty array)')
  }
  if (!isPlainObject(raw.validationRules)) {
    throw new InvalidUiConfigError('ui_config.validationRules is required (object)')
  }
  return { ...(raw as unknown as UiConfig), id, version }
}

/**
 * ``GET /config/ui/{org}`` may include a top-level ``client_name`` in addition
 * to the microapp ``ui_config`` object. When the web-ordering slice omits
 * ``clientName``, use that envelope field so the navbar can render on first
 * paint without a second request.
 */
export const mergeClientNameFromApiEnvelope = (config: UiConfig, clientName: string | null | undefined): UiConfig => {
  const fromEnvelope = typeof clientName === 'string' ? clientName.trim() : ''
  if (!fromEnvelope) {
    return config
  }
  const existing = config.clientName?.trim()
  if (existing) {
    return config
  }
  return { ...config, clientName: fromEnvelope }
}

/**
 * Process-local cache of the loaded ``UiConfig`` for non-React callers
 * (canonical-order builder, Epic callback). Always set by ``UiConfigProvider``
 * /``loadEpicUiConfig`` before authenticated routes render. Returns ``null``
 * before load completes; consumers running inside the React tree should read
 * from the ``useUiConfig()`` hook (which is gated by the fail-loud provider)
 * rather than this singleton.
 */
let activeUiConfig: UiConfig | null = null

export const setActiveUiConfig = (config: UiConfig): void => {
  activeUiConfig = config
}

export const getActiveUiConfig = (): UiConfig | null => activeUiConfig

/** Test-only: reset between specs. */
export const __resetActiveUiConfigForTests = (): void => {
  activeUiConfig = null
}

const resolveConfig = (config: UiConfig | undefined): UiConfig | null => config ?? activeUiConfig

/**
 * Semantic accessor for the three requisition consents.
 *
 * Reads the canonical microapp ``patientConsents`` array (positional:
 * 0 = Incidental Findings, 1 = NY Sample Retention, 2 = Research
 * Participation Contact). Returns ``undefined`` for any slot the source did
 * not provide so downstream ``isApplicable`` checks short-circuit cleanly.
 */
export const getPatientConsents = (
  config?: UiConfig
): {
  incidental: IncidentalFindingsConsentConfig | undefined
  ny: NewYorkSampleRetentionConsentConfig | undefined
  research: ResearchParticipationContactConsentConfig | undefined
} => {
  const cfg = resolveConfig(config)
  const arr = cfg?.patientConsents
  if (Array.isArray(arr) && arr.length > 0) {
    return {
      incidental: arr[0] as IncidentalFindingsConsentConfig | undefined,
      ny: arr[1] as NewYorkSampleRetentionConsentConfig | undefined,
      research: arr[2] as ResearchParticipationContactConsentConfig | undefined,
    }
  }
  // Older ui_config: flat optional blocks when ``patientConsents`` is absent or empty.
  if (
    cfg?.incidentalFindingsConsent ||
    cfg?.newYorkSampleRetentionConsent ||
    cfg?.researchParticipationContactConsent
  ) {
    return {
      incidental: cfg.incidentalFindingsConsent,
      ny: cfg.newYorkSampleRetentionConsent,
      research: cfg.researchParticipationContactConsent,
    }
  }
  return { incidental: undefined, ny: undefined, research: undefined }
}

/**
 * Coerce a consent section's ``requireInteraction`` gating hint to a strict
 * boolean. Missing / ``null`` / non-boolean values resolve to ``false`` so the
 * default preserves today's behavior (no forced interaction). Accepts the
 * union config shapes as well as the raw ``PatientConsentItem`` so every
 * consumer reads the flag the same way.
 */
export const consentRequiresInteraction = (
  section: { requireInteraction?: boolean | null } | null | undefined
): boolean => section?.requireInteraction === true

export const getTestTypesGroupedByCategory = (config?: UiConfig): Map<string, UiTestTypeOption[]> => {
  const grouped = new Map<string, UiTestTypeOption[]>()
  const cfg = resolveConfig(config)
  if (!cfg) return grouped
  for (const testType of cfg.testTypes) {
    const existing = grouped.get(testType.category) ?? []
    existing.push(testType)
    grouped.set(testType.category, existing)
  }
  return grouped
}

export const getTestTypeConfig = (testType: TestType, config?: UiConfig): UiTestTypeOption | undefined => {
  const cfg = resolveConfig(config)
  return cfg?.testTypes?.find((item) => item.value === testType)
}

/**
 * Whether a resolved test-type SKU belongs to the Ultra-Rapid (urWGS) product
 * line (proband / duo / trio / quad), as opposed to Rapid (rWGS) or none.
 *
 * Discriminates on the stable ``TestType`` enum value rather than the
 * config-driven category string (whose spelling varies, e.g. "Ultra Rapid"
 * vs "Ultra-Rapid"). Used to scope Ultra-Rapid-only behavior such as the
 * post-accession comparator-addition window.
 */
export const isUltraRapidTestType = (testType: TestType | string | undefined | null): boolean => {
  return (
    testType === TestType.urWGS_Patient_Only ||
    testType === TestType.urWGS_Duo ||
    testType === TestType.urWGS_Trio ||
    testType === TestType.urWGS_Quad
  )
}

/**
 * Maximum number of biological-parent comparators (`parentSamplesRequired`) any
 * test type in `category` supports — i.e. the product line's parent capability,
 * decoupled from the currently-selected tier.
 *
 * R5 sequencing decoupling: a category resolves to its proband-only SKU in the
 * Sequencing Options modal, so OrderStart must gate the family section on this
 * category max (not the resolved SKU's own tier) to keep the "add a parent"
 * entry point available before any parent is selected. Returns 0 when the
 * category is unknown or has no parent-capable SKU.
 */
export const getMaxParentSamplesForCategory = (category: string | undefined, config?: UiConfig): number => {
  const cfg = resolveConfig(config)
  if (!cfg || !category) return 0
  let max = 0
  for (const testType of cfg.testTypes) {
    if (testType.category === category && testType.parentSamplesRequired > max) {
      max = testType.parentSamplesRequired
    }
  }
  return max
}

/**
 * Resolve the test type SKU in `category` whose `parentSamplesRequired` equals
 * `tier` (0 = proband, 1 = duo, 2 = trio).
 *
 * R5 sequencing decoupling: the user picks a *speed* (category) and a family
 * structure (comparators) independently; the submitted SKU must follow the
 * comparator-derived tier so a duo family bills the duo SKU, not the proband
 * SKU it resolved to on speed selection. Returns `undefined` when the category
 * has no SKU at that tier (caller should keep the current selection rather than
 * silently mis-bill).
 */
export const resolveTestTypeForCategoryTier = (
  category: string | undefined,
  tier: number,
  config?: UiConfig
): TestType | undefined => {
  const cfg = resolveConfig(config)
  if (!cfg || !category) return undefined
  return cfg.testTypes.find((t) => t.category === category && t.parentSamplesRequired === tier)?.value
}

export const getTurnaroundTimeDayBasisForTestType = (
  testTypeConfig: UiTestTypeOption | undefined
): TurnaroundTimeDayBasis => {
  return normalizeTurnaroundTimeDayBasis(testTypeConfig?.turnaroundTimeDayBasis ?? DEFAULT_TURNAROUND_TIME_DAY_BASIS)
}

/** Primary line on the Test Options card for turnaround. */
export const formatTurnaroundTimeCardTitle = (days: number, basis: TurnaroundTimeDayBasis): string => {
  const dayWord = days === 1 ? 'day' : 'days'
  const basisAdj = basis === 'calendar' ? 'calendar' : 'business'
  return `${days} ${basisAdj} ${dayWord} turnaround time`
}

/** Secondary line on the Sequencing Options summary grid (Physician Ordering R5). */
export const formatTurnaroundTimeSequencingCardSubtitle = (days: number, basis: TurnaroundTimeDayBasis): string => {
  return `Typically ${formatTurnaroundTimeOptionLabel(days, basis)} after sample receipt`
}

/** Subtitle under the Test Type heading in TestOptionsModal (per-option labels carry calendar vs business). */
export const formatTurnaroundTimeTestTypeSectionDescription = (): string => {
  return 'Turnaround time varies by test type.'
}

/** Short turnaround fragment for modal option labels, e.g. "3 business days". */
export const formatTurnaroundTimeOptionLabel = (days: number, basis: TurnaroundTimeDayBasis): string => {
  const dayWord = days === 1 ? 'day' : 'days'
  const basisAdj = basis === 'calendar' ? 'calendar' : 'business'
  return `${days} ${basisAdj} ${dayWord}`
}

/**
 * Returns the default test type value for new orders (the one with isDefault: true,
 * or the first available). Returns ``TestType.NONE`` when no config is loaded — the
 * caller should treat that as "no selection" since the fail-loud provider blocks
 * authenticated route renders before config is available.
 */
export const getDefaultTestType = (config?: UiConfig): TestType => {
  const cfg = resolveConfig(config)
  if (!cfg) return TestType.NONE
  const defaultOption = cfg.testTypes?.find((t) => t.isDefault)
  if (defaultOption) return defaultOption.value
  return cfg.testTypes?.[0]?.value ?? TestType.NONE
}

/** Biological-parent tier implied by a canonical `orderType` (2 = trio, 1 = duo, 0 = proband). */
const orderTypeToParentTier = (orderType: string | undefined): number => {
  const ot = (orderType ?? 'PROBAND_ONLY').trim()
  if (ot === 'FAMILY_TRIO') return 2
  if (ot === 'FAMILY_DUO_MOTHER' || ot === 'FAMILY_DUO_FATHER') return 1
  return 0
}

/**
 * Recover a usable `order.selectedOption` (test-type SKU) for a canonical whose
 * stored value is null/blank (GEN-3xxx). Some orders were persisted before the
 * create-path seed reliably wrote `selectedOption`; on reload they carry no test
 * type, so `getTestTypeConfig` returns undefined and comparator editing stays
 * disabled forever.
 *
 * This is a **UI-gating recovery only** — the server derives and enforces the
 * authoritative SKU on submit. We never overwrite a real selection: a non-blank
 * `selectedOption` is returned unchanged. Otherwise we derive the parent tier
 * from the order's own `orderType` (the same signal submit uses) and resolve the
 * SKU at that tier within the org's default test-type category. Returns
 * `undefined` when nothing resolves (no config / unknown category / no SKU at
 * that tier) so callers keep the blank value rather than fabricate a SKU.
 */
export const recoverSelectedOptionForCanonical = (
  canonical: { order?: { selectedOption?: string | null } | null; orderType?: string | null } | null | undefined,
  config?: UiConfig
): TestType | undefined => {
  const existing = canonical?.order?.selectedOption?.trim()
  if (existing) return existing as TestType
  const cfg = resolveConfig(config)
  if (!cfg) return undefined
  const defaultTestType = getDefaultTestType(cfg)
  const defaultCategory = cfg.testTypes?.find((t) => t.value === defaultTestType)?.category
  if (!defaultCategory) return undefined
  const tier = orderTypeToParentTier(canonical?.orderType ?? undefined)
  return resolveTestTypeForCategoryTier(defaultCategory, tier, cfg)
}

/**
 * Returns the configured lab name for the active organization. Returns an
 * empty string when no config is loaded; callers should treat that as a
 * pre-load state (the fail-loud provider blocks renders before config is
 * available, so this only happens for non-React callers running before load).
 */
export const getLabName = (config?: UiConfig): string => {
  const cfg = resolveConfig(config)
  if (!cfg) return ''
  return cfg.wetLab?.labName?.trim() ?? ''
}

/**
 * CMS CLIA certificate number for the primary wet lab (``UiConfig.wetLab``, from microapp ``WET_LAB``).
 * Empty string when unset; TRF fields stay blank.
 */
export const getCliaLabId = (config?: UiConfig): string => {
  const cfg = resolveConfig(config)
  if (!cfg) return ''
  return cfg.wetLab?.cliaLabId?.trim() ?? ''
}

/**
 * Resolve the order fulfillment strategy for the active organization.
 *
 * Prefers the explicit ``wetLab.fulfillmentStrategy`` (set by the server). When
 * absent (older configs), infers from ``wetLab.labName`` using the same rules as
 * the server resolver: names containing ``rady`` / ``rcigm`` map to ``'rcigm'``;
 * names containing ``inflection`` / ``lsmc`` / ``seqone`` map to
 * ``'inflection_test'``. Defaults to ``'rcigm'`` (the safe default: no cancel
 * flow) when nothing matches or no config is loaded.
 */
export const getFulfillmentStrategy = (config?: UiConfig): 'rcigm' | 'inflection_test' => {
  const cfg = resolveConfig(config)
  const explicit = cfg?.wetLab?.fulfillmentStrategy
  if (explicit === 'rcigm' || explicit === 'inflection_test') {
    return explicit
  }
  const labName = cfg?.wetLab?.labName?.toLowerCase() ?? ''
  if (labName.includes('rady') || labName.includes('rcigm')) {
    return 'rcigm'
  }
  if (labName.includes('inflection') || labName.includes('lsmc') || labName.includes('seqone')) {
    return 'inflection_test'
  }
  return 'rcigm'
}

/** Which lab's Clinical Summary design applies: Rady frames for ``rcigm``, SeqOne frames for every other lab. */
export type PhenotypeModel = 'rady' | 'seqone'

export const getPhenotypeModel = (config?: UiConfig): PhenotypeModel =>
  getFulfillmentStrategy(config) === 'rcigm' ? 'rady' : 'seqone'

/**
 * Single UI gate for the Cancel Order flow. Both fulfillment strategies now
 * support cancellation, so a resolved strategy always permits the flow — the
 * per-strategy *status* window (see :func:`isCancellableStatusForStrategy`)
 * decides when the kebab is actually shown. ``getFulfillmentStrategy`` always
 * resolves to a concrete strategy, so this is effectively always true; kept as
 * an explicit gate so callers read intent and future strategies can opt out.
 */
export const canCancelOrder = (config?: UiConfig): boolean => {
  const strategy = getFulfillmentStrategy(config)
  return strategy === 'inflection_test' || strategy === 'rcigm'
}

/**
 * Per-strategy status window for the Cancel Order kebab. Mirrors the
 * authoritative backend cancel gates so the SPA never offers a cancel the
 * backend will reject:
 *
 * - ``inflection_test`` (LSMC): cancellable at any status **except** already
 *   ``CANCELLED``.
 * - ``rcigm`` (Rady): cancellable only **before accessioning** —
 *   ``ORDER_PLACED`` or ``ORDER_SUBMITTED`` — because Rady only accepts
 *   cancellation while the test is ``Registered`` / ``Hold``. Once
 *   ``SAMPLE_RECEIVED`` (accessioned) or ``RESULT_READY``, Rady rejects it.
 */
export const isCancellableStatusForStrategy = (
  strategy: FulfillmentStrategy,
  orderStatus: OrderStatusDisplay
): boolean => {
  if (orderStatus === OrderStatusDisplay.CANCELLED) {
    return false
  }
  if (strategy === 'rcigm') {
    return orderStatus === OrderStatusDisplay.ORDER_PLACED || orderStatus === OrderStatusDisplay.ORDER_SUBMITTED
  }
  return true
}

/**
 * Cell D — UX gating for ``inflection_test`` orders.
 *
 * The rules below mirror the authoritative provider-api gate (Cell C, which
 * returns HTTP 409). They exist purely so the SPA does not offer actions the
 * backend will reject. **Every helper is a no-op for non-inflection_test
 * (rcigm) orders**: those keep today's behavior (always addable / editable /
 * removable), so callers can layer these gates on top of the existing status
 * allowlist without changing rcigm UX.
 *
 * All helpers take an explicit ``strategy`` (from :func:`getFulfillmentStrategy`)
 * rather than the whole config so they stay pure and trivially unit-testable.
 */
export type FulfillmentStrategy = 'rcigm' | 'inflection_test'

/** Add-comparator window: 7 days after the newest final report's `generated_at`. */
export const ADD_COMPARATOR_WINDOW_DAYS = 7
const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Whether adding a comparator is still allowed for an ``inflection_test`` order.
 *
 * Rule: allowed until 7 days after the newest FINAL report's ``generated_at``
 * (inclusive of the 7th day — i.e. addable while ``now <= generated_at + 7d``).
 * When there is no final report (or its ``generated_at`` is missing/unparseable)
 * adding is always allowed. Reuses :func:`selectFinalGuidanceReport` for the
 * newest-final selection so this never re-implements final-report logic.
 *
 * No-op for ``rcigm``: always returns ``true``.
 */
export const canAddComparators = (
  strategy: FulfillmentStrategy,
  reports: ReadonlyArray<OrderReportResponse>,
  now: Date = new Date()
): boolean => {
  if (strategy !== 'inflection_test') return true
  const finalReport = selectFinalGuidanceReport(reports)
  const generatedAt = finalReport?.generated_at
  if (!generatedAt) return true
  const generatedMs = new Date(generatedAt).getTime()
  if (!Number.isFinite(generatedMs)) return true
  const deadlineMs = generatedMs + ADD_COMPARATOR_WINDOW_DAYS * MS_PER_DAY
  return now.getTime() <= deadlineMs
}

/**
 * Whether the order's SeqOne HPO lock is engaged. Reads the order's
 * ``seqoneHPOLock`` flag; missing/undefined is treated as NOT locked. Only
 * meaningful for ``inflection_test`` — ``rcigm`` is never locked.
 */
export const isSeqoneHpoLocked = (strategy: FulfillmentStrategy, seqoneHPOLock?: boolean | null): boolean => {
  if (strategy !== 'inflection_test') return false
  return seqoneHPOLock === true
}

/**
 * Whether HPO editing is allowed on an EXISTING/persisted member (the proband
 * or a comparator already saved on the order). For ``inflection_test`` this is
 * blocked when the order's SeqOne HPO lock is engaged; for ``rcigm`` (or when
 * unlocked) it is always allowed. This is the HPO-specific gate layered on top
 * of the normal status editability check the caller already applies.
 */
export const canEditExistingMemberHpo = (strategy: FulfillmentStrategy, seqoneHPOLock?: boolean | null): boolean =>
  !isSeqoneHpoLocked(strategy, seqoneHPOLock)

/**
 * HPO on a comparator ADDED in the current edit session (not yet persisted) is
 * always editable regardless of the lock — the lock only freezes members that
 * were already on the order. Always ``true``; kept as a named helper so the
 * newly-added path documents that it intentionally ignores the lock.
 */
export const canEditNewComparatorHpo = (_strategy: FulfillmentStrategy): boolean => true

/** TRF specimen-row wet lab; always from `wetLab` (not from ordering session). */
export interface TrfWetLabFields {
  labName: string
  cliaLabId: string
}

export const getTrfWetLabFields = (config?: UiConfig): TrfWetLabFields => ({
  labName: getLabName(config),
  cliaLabId: getCliaLabId(config),
})

/** TRF Section B address lines from ``UiConfig.orgAddress`` (microapp ``CONTACTS``). */
export const getTrfOrgAddress = (config?: UiConfig): UiOrgAddressConfig => {
  const cfg = resolveConfig(config)
  const raw = cfg?.orgAddress
  if (!raw || typeof raw !== 'object') return {}
  const out: UiOrgAddressConfig = {}
  for (const k of ['street', 'street2', 'city', 'state', 'zip', 'country'] as const) {
    const v = raw[k]
    if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  }
  return out
}

/**
 * Wet-lab ship-to address (``UiConfig.wetLab.shipToAddress``) used as the
 * ``addressTo`` for kit shipping labels. Trims each present string field and
 * drops empties. Returns ``{}`` when the config has no ship-to block so
 * callers can treat "not configured" and "empty" uniformly (the shipping-label
 * handler then reports a config error rather than calling the endpoint).
 */
export const getShipToAddress = (config?: UiConfig): UiAddressConfig => {
  const cfg = resolveConfig(config)
  const raw = cfg?.wetLab?.shipToAddress
  if (!raw || typeof raw !== 'object') return {}
  const out: UiAddressConfig = {}
  for (const k of ['name', 'street', 'street2', 'city', 'state', 'zip', 'country', 'phone', 'email'] as const) {
    const v = raw[k]
    if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  }
  return out
}

/**
 * Resolve the tenant's per-order reanalysis cap from ``wetLab.reanalysisLimit``,
 * re-applying the fail-safe coercions the server guarantees so consumers behave
 * identically on legacy payloads that predate the block:
 *
 * - ``enforceReanalysisMaximumCount`` is ``true`` only when the source is a real
 *   boolean ``true``; anything else (missing, ``null``, the string ``"true"``)
 *   resolves to ``false``.
 * - ``maximumReanalysisCount`` defaults to ``0`` when missing, ``null``, a
 *   boolean, or a non-integer. A missing cap is a hard ``0``, not "unlimited".
 *
 * Always returns a fully-populated object so callers never null-check.
 *
 * NOTE: Informational only today — enforcement is not yet wired server-side, so
 * the API still allows reanalysis regardless of ``maximumReanalysisCount``.
 */
export const getReanalysisLimit = (config?: UiConfig): Required<UiReanalysisLimitConfig> => {
  const raw = resolveConfig(config)?.wetLab?.reanalysisLimit
  const enforce = raw?.enforceReanalysisMaximumCount === true
  const rawCount = raw?.maximumReanalysisCount
  const maximumReanalysisCount = typeof rawCount === 'number' && Number.isInteger(rawCount) ? rawCount : 0
  return { enforceReanalysisMaximumCount: enforce, maximumReanalysisCount }
}

/**
 * Whether the per-order reanalysis cap is enforced for the active org. Fails
 * safe to ``false`` for legacy payloads / non-boolean sources. Informational
 * only today (see :func:`getReanalysisLimit`).
 */
export const isReanalysisMaximumEnforced = (config?: UiConfig): boolean =>
  getReanalysisLimit(config).enforceReanalysisMaximumCount

/**
 * Max reanalyses permitted per order. Defaults to ``0`` (a hard cap, not
 * "unlimited") when missing/invalid. Informational only today (see
 * :func:`getReanalysisLimit`).
 */
export const getMaximumReanalysisCount = (config?: UiConfig): number =>
  getReanalysisLimit(config).maximumReanalysisCount

/**
 * Reason a new reanalysis request is blocked, or ``null`` when it is allowed.
 *
 * - ``'in-flight'`` — a reanalysis is already running for the order
 *   (``reanalysis_in_flight === true``); a new one is disabled regardless of the
 *   cap.
 * - ``'cap-reached'`` — the tenant enforces the cap
 *   (``enforceReanalysisMaximumCount``) and the order's ``reanalysis_count`` has
 *   reached the ``maximumReanalysisCount``.
 */
export type ReanalysisBlockReason = 'in-flight' | 'cap-reached'

/**
 * Decide whether requesting a NEW reanalysis should be blocked/disabled, pairing
 * the order's live count (GET /orders/{id}/reanalysis-count) with the tenant's
 * ``wetLab.reanalysisLimit`` config (GET /config/ui).
 *
 * Rules (per the reanalysis-count contract):
 *
 * 1. If a reanalysis is already in flight, block regardless of the cap
 *    (``'in-flight'``) — one is already running.
 * 2. Otherwise, when the cap is enforced, block once
 *    ``reanalysis_count >= maximumReanalysisCount`` (``'cap-reached'``).
 * 3. When the cap is NOT enforced, never block on the count.
 *
 * Returns ``null`` when a new reanalysis is allowed. Fail-safe on a missing count
 * payload: ``undefined`` is treated as "no signal" → not blocked (the caller
 * should keep the control enabled until the count loads rather than block on
 * absent data). The in-flight rule is applied first so it wins even when the cap
 * is disabled.
 *
 * NOTE: Enforcement is UI-side only today — the backend still allows a
 * reanalysis regardless of the cap, so this helper is the current gate.
 */
export const reanalysisBlockReason = (
  count: ReanalysisCountResponse | null | undefined,
  config?: UiConfig
): ReanalysisBlockReason | null => {
  if (!count) return null
  if (count.reanalysis_in_flight === true) return 'in-flight'
  const { enforceReanalysisMaximumCount, maximumReanalysisCount } = getReanalysisLimit(config)
  if (enforceReanalysisMaximumCount && count.reanalysis_count >= maximumReanalysisCount) {
    return 'cap-reached'
  }
  return null
}

/**
 * Whether requesting a NEW reanalysis should be blocked/disabled for the order.
 * Thin boolean wrapper over :func:`reanalysisBlockReason` — see it for the exact
 * rules and fail-safe behavior.
 */
export const shouldBlockNewReanalysis = (
  count: ReanalysisCountResponse | null | undefined,
  config?: UiConfig
): boolean => reanalysisBlockReason(count, config) !== null

/**
 * True when the microapp ``CONTACTS`` / ``orgAddress.state`` resolves to New
 * York. Used for UX defaults (e.g. NY sample retention disclosure expanded for
 * in-state organizations).
 */
export function isUiOrgHeadquarteredInNewYork(config?: UiConfig): boolean {
  const raw = getTrfOrgAddress(config).state?.trim()
  if (!raw) return false
  const n = raw.toLowerCase().replace(/\./g, '')
  return n === 'ny' || n === 'new york'
}

/** Bundle passed to TRF PDF renderers (wet lab + org address from ui_config). */
export interface TrfPdfConfigFields {
  wetLab: TrfWetLabFields
  orgAddress: UiOrgAddressConfig
}

export const getTrfPdfConfigFields = (config?: UiConfig): TrfPdfConfigFields => ({
  wetLab: getTrfWetLabFields(config),
  orgAddress: getTrfOrgAddress(config),
})

/**
 * Returns the microapp-managed client display name (``clientName``) for the
 * navbar / header. Returns ``null`` when no config is loaded or the field
 * is unset. Also checks :func:`getActiveUiConfig` when the passed config has
 * no name so a brief context/singleton mismatch does not hide the org chip.
 */
export const getClientName = (config?: UiConfig): string | null => {
  const pick = (c: UiConfig | null | undefined): string | null => {
    const name = c?.clientName
    return typeof name === 'string' && name.trim().length > 0 ? name.trim() : null
  }
  return pick(resolveConfig(config)) ?? pick(getActiveUiConfig())
}

/**
 * Whether the org has the CCS advisory coverage check enabled. Single flag that
 * turns the CCS UI on/off for an org (admin CCS eligibility toggle + clinician
 * list column). Reads the contract path ``ui_config.coverage.ccsCheckEnabled``;
 * a missing/malformed ``coverage`` block fails open to disabled (false).
 */
export const isCcsCheckEnabled = (config?: UiConfig): boolean => {
  return resolveConfig(config)?.coverage?.ccsCheckEnabled === true
}

/**
 * Whether Case Connect (Clinician Connect) is enabled for this org's members.
 * Opt-in per org (absent/unset -> disabled), mirroring guidance/kitOrdering.
 * Gates only ORG-MEMBER entry points; org-less external participants are NOT
 * subject to this flag (they have no client config) and reach their case via
 * permissions alone.
 */
export const isCaseConnectEnabled = (config?: UiConfig): boolean => {
  return resolveConfig(config)?.featureFlags?.caseConnect === true
}

/**
 * Resolve the tenant's post-submit comparator-addition window config.
 *
 * Reads ``ui_config.editabilityWindow``. The block is optional (absent on
 * unmigrated tenant configs and on deployed microapp configs until they add
 * it), so a missing/malformed object resolves to ``undefined`` and callers
 * default to disabled.
 */
export const getEditabilityWindow = (config?: UiConfig): EditabilityWindowConfig | undefined => {
  const raw = resolveConfig(config)?.editabilityWindow
  return raw && typeof raw === 'object' ? raw : undefined
}

/**
 * Whether the post-submit comparator-addition window is enabled for the tenant.
 *
 * Fails safe to ``false`` (disabled) when the ``editabilityWindow`` object is
 * missing — not all tenant configs are migrated, and deployed envs may not
 * carry the field yet.
 *
 * NOTE: This is plumbing only and does NOT yet gate any UI. The pre-existing
 * ``featureFlags.allowPostSubmitComparatorEdits`` flag remains the active gate;
 * which one drives the comparator-add UI going forward is TBD with the team.
 */
export const isComparatorAdditionWindowEnabled = (config?: UiConfig): boolean => {
  return getEditabilityWindow(config)?.editabilityWindowEnabled === true
}

/**
 * Number of hours after order submit during which comparators may still be
 * added, per ``editabilityWindow.comparatorAdditionWindowHours``. Returns
 * ``undefined`` when the window is disabled or the value is missing/invalid so
 * callers can distinguish "no window configured" from a real duration.
 */
export const getComparatorAdditionWindowHours = (config?: UiConfig): number | undefined => {
  const window = getEditabilityWindow(config)
  if (!window || window.editabilityWindowEnabled !== true) return undefined
  const hours = window.comparatorAdditionWindowHours
  return typeof hours === 'number' && Number.isFinite(hours) ? hours : undefined
}

/**
 * Whether the org tracks ordering sites (GEN-5738), reading the contract path
 * ``ui_config.organizationSiteInformation.trackOrderSite``. When true the SPA
 * renders a REQUIRED ordering-site dropdown and the provider-api enforces the
 * site at draft-create + allowlist-validates it on create/patch.
 *
 * Fails safe to ``false`` (disabled) when the block is missing/malformed —
 * non-CHCM tenants and unmigrated configs omit it and must be unchanged.
 */
export const isOrderSiteTrackingEnabled = (config?: UiConfig): boolean => {
  return resolveConfig(config)?.organizationSiteInformation?.trackOrderSite === true
}

/**
 * Resolve the configured ordering-site names for the org's required dropdown.
 * Options are sourced from ``organizationSiteInformation.organizationSites[].siteName``
 * (never hardcoded); returns a de-duplicated, blank-filtered list in config order.
 * Returns an empty array when tracking is off or no sites are configured.
 *
 * This same list is the client-side allowlist used to validate the selection
 * before create/patch, guarding against the stale-pinned-config 422 the
 * provider-api can still return.
 */
export const getOrganizationSiteNames = (config?: UiConfig): string[] => {
  const info = resolveConfig(config)?.organizationSiteInformation
  if (!info || info.trackOrderSite !== true || !Array.isArray(info.organizationSites)) {
    return []
  }
  const seen = new Set<string>()
  const names: string[] = []
  for (const site of info.organizationSites) {
    const name = typeof site?.siteName === 'string' ? site.siteName.trim() : ''
    if (name && !seen.has(name)) {
      seen.add(name)
      names.push(name)
    }
  }
  return names
}

/**
 * Whether ``siteName`` is a currently-configured ordering site for the org
 * (client-side allowlist check). Case-sensitive exact match against
 * :func:`getOrganizationSiteNames`, mirroring the provider-api allowlist so the
 * SPA blocks an invalid selection before the create/patch 422.
 */
export const isValidOrganizationSite = (siteName: string | null | undefined, config?: UiConfig): boolean => {
  const name = typeof siteName === 'string' ? siteName.trim() : ''
  if (!name) return false
  return getOrganizationSiteNames(config).includes(name)
}

/**
 * Whether the org participates in the DocuSign TRF e-signature flow, mirroring
 * provider-api's ``docusign_enabled`` gate (``INFLECTION_WEB_ORDERING.docusign.enabled``).
 *
 * This is the durable signal the TRF signature poll keys off: when true, a
 * submitted order will receive a signature envelope, so the SPA polls the
 * "preparing" window until the envelope reaches ``sent``. Fails closed
 * (``false``) when the ``docusign`` block or ``enabled`` flag is missing — an
 * un-migrated org must not spin the poll. Requires a real boolean ``true``
 * (matching the server, which treats a stringly-typed ``"true"`` as disabled).
 */
export const isTrfSignatureDocusignEnabled = (config?: UiConfig): boolean => {
  return resolveConfig(config)?.docusign?.enabled === true
}

export const applyThemeTokens = (tokens?: ThemeTokens) => {
  if (!tokens) return
  const root = document.documentElement

  if (tokens.brandPrimary) {
    root.style.setProperty('--color-brand-600', tokens.brandPrimary)
    root.style.setProperty('--color-brand-500', tokens.brandPrimary)
    root.style.setProperty('--color-border-brand', tokens.brandPrimary)
    root.style.setProperty('--color-text-brand', tokens.brandPrimary)
  }

  if (tokens.brandStrong) {
    root.style.setProperty('--color-brand-700', tokens.brandStrong)
    root.style.setProperty('--color-bg-brand-strong', tokens.brandStrong)
  }

  if (tokens.textBrand) {
    root.style.setProperty('--color-text-brand', tokens.textBrand)
  }

  if (tokens.accent) {
    root.style.setProperty('--color-info-500', tokens.accent)
    root.style.setProperty('--color-info-600', tokens.accent)
  }
}
